import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import HostLink from '../components/HostLink'
import { unsubscribe } from '../api'
import Button from '../components/Button'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'

/**
 * Unsubscribe landing page — the target of the Unsubscribe link in every
 * digest email footer (/unsubscribe?client_id=...).
 *
 * Deliberately a confirm-button page rather than acting on page load:
 * corporate email scanners prefetch every link in an email, and an
 * unsubscribe that fires on GET would silently unsubscribe clients whose
 * mail gateway "clicked" the link. The button does a POST, which scanners
 * don't issue. No login required (CAN-SPAM).
 */
export default function Unsubscribe() {
  useDocumentTitle('Unsubscribe')
  const [params] = useSearchParams()
  const clientId = params.get('client_id')

  const [done, setDone]       = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError]     = useState('')
  const [busy, setBusy]       = useState(false)

  async function confirm() {
    setBusy(true)
    setError('')
    try {
      const res = await unsubscribe(clientId)
      setMessage(res.message)
      setDone(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {!clientId ? (
          <>
            <div className="text-4xl mb-4">🤔</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Missing unsubscribe link details</h1>
            <p className="text-sm text-gray-500 mb-6">
              This page needs the link from a Praenum digest email. Please use the
              Unsubscribe link at the bottom of your latest digest, or sign in and
              manage email settings from your Preferences page.
            </p>
            <Link to="/signin" className="text-sm font-medium text-brand-500 hover:underline">
              Sign in →
            </Link>
          </>
        ) : done ? (
          <>
            <div className="text-4xl mb-4">✅</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">You're unsubscribed</h1>
            <p className="text-sm text-gray-500 mb-6">{message}</p>
            <HostLink to="/" className="text-sm font-medium text-brand-500 hover:underline">
              Back to Praenum →
            </HostLink>
          </>
        ) : (
          <>
            <div className="text-4xl mb-4">📭</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Unsubscribe from digests?</h1>
            <p className="text-sm text-gray-500 mb-6">
              You'll stop receiving the weekly grant digest. Your account and match
              history stay intact, and you can resubscribe any time from your
              Preferences page.
            </p>
            <ErrorAlert message={error} />
            <div className="flex flex-col gap-3 items-center mt-2">
              <Button onClick={confirm} loading={busy} variant="primary">
                Yes, unsubscribe me
              </Button>
              <HostLink to="/" className="text-sm text-gray-500 hover:text-gray-700">
                Never mind, keep my digests
              </HostLink>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
